/**
 * EditorBand — piezas de la banda única del editor (patrón V2, paso 2 de
 * Diseño): todo lo que se usa mientras se edita cabe en una sola banda.
 *
 * - BandTool: modo con ICONO + TEXTO visible (el usuario necesita saber en
 *   qué modo está).
 * - BandIconButton: icono solo, cuadrado (vistas de viewport), con title.
 * - BandTrigger: disparador de dropdown (página actual + pill + chevron).
 */
import type { ReactNode } from 'react';

export function BandTool({ icon, label, active, onClick }: {
  icon: ReactNode; label: string; active: boolean; onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: '6px',
        height: 'var(--evo-editor-tool-h)',
        padding: '0 var(--evo-control-px)',
        boxSizing: 'border-box',
        borderRadius: 'var(--evo-r)',
        background: active ? 'var(--evo-cyan-12)' : 'transparent',
        color: active ? 'var(--evo-cyan)' : 'var(--evo-fg-2)',
        border: `1px solid ${active ? 'var(--evo-cyan)' : 'transparent'}`,
        fontFamily: 'var(--evo-font)', fontSize: 'var(--evo-fs-label)',
        fontWeight: 'var(--evo-fw-medium)', lineHeight: 1,
        cursor: 'pointer', whiteSpace: 'nowrap',
      }}
    >
      {icon}{label}
    </button>
  );
}

export function BandIconButton({ icon, title, active, onClick, loading = false }: {
  icon: ReactNode; title: string; active: boolean; onClick: () => void;
  /** Proceso en curso: el icono gira y se pinta en cian; el botón no acepta
   *  otro clic hasta terminar. */
  loading?: boolean;
}) {
  return (
    <button
      type="button"
      title={title}
      onClick={loading ? undefined : onClick}
      disabled={loading}
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        width: 'var(--evo-editor-tool-h)', height: 'var(--evo-editor-tool-h)',
        boxSizing: 'border-box', borderRadius: 'var(--evo-r)',
        background: active || loading ? 'var(--evo-cyan-12)' : 'transparent',
        color: active || loading ? 'var(--evo-cyan)' : 'var(--evo-fg-3)',
        border: `1px solid ${active || loading ? 'var(--evo-cyan)' : 'transparent'}`,
        cursor: loading ? 'progress' : 'pointer',
      }}
    >
      <span style={{ display: 'inline-flex', animation: loading ? 'evo-spin 0.8s linear infinite' : 'none' }}>
        {icon}
      </span>
    </button>
  );
}

export function BandTrigger({ children, onClick }: { children: ReactNode; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', gap: '6px',
        height: 'var(--evo-editor-tool-h)',
        padding: '0 var(--evo-control-px-sm)',
        boxSizing: 'border-box',
        borderRadius: 'var(--evo-r)',
        background: 'var(--evo-bg-3)',
        border: '1px solid var(--evo-stroke)',
        color: 'var(--evo-fg-2)',
        fontFamily: 'var(--evo-font)', fontSize: 'var(--evo-fs-label)',
        cursor: 'pointer', whiteSpace: 'nowrap',
        flexShrink: 0, minWidth: '170px',
      }}
    >
      {children}
    </button>
  );
}
